import React from 'react';

import { graphql, useStaticQuery } from 'gatsby';

import { useSiteMetadata } from '../../hooks/use-site-metadata';
import { getJsonLd } from '../../misc/ldJson';
import { removeTrailingSlash } from '../../misc/utilities';

interface BreadcrumbItem {
	title: string;
	to: string;
}

interface Appointment {
	start: string;
	end: string;
	location: string;
}

interface Course {
	title: string;
	courseTypeTitle: string;
	courseTypeUuid: string;
	price: number;
	seo: {
		description: string;
	};
	appointments: Appointment[];
}

interface SearchEngineOptimizationProps {
	title?: string;
	description?: string;
	/**
	 * The relative path to the image file.
	 */
	imagePath?: string;
	path: string;
	breadcrumbItems?: BreadcrumbItem[];
	courses?: Course[];
	noIndex?: boolean;
	blogPost?: any; // You might want to define a more specific type for blogPost
}

export const SearchEngineOptimization: React.FC<SearchEngineOptimizationProps> = ({
	title = '',
	description = '',
	imagePath,
	path,
	breadcrumbItems = [],
	courses = [],
	noIndex = false,
	blogPost,
}) => {
	const siteMetadata = useSiteMetadata();
	const {
		logo: { publicURL: logoPath },
		courseTypes: { edges: courseTypes },
	} = useStaticQuery(graphql`
		query SEO {
			logo: file(absolutePath: { regex: "/src/images/logo.?png/" }) {
				publicURL
			}

			courseTypes: allMarkdownRemark(
				filter: { fileAbsolutePath: { regex: "//content/kurstypen//" } }
			) {
				edges {
					node {
						id
						frontmatter {
							uuid
							slug
							title
							heroImage {
								image {
									publicURL
								}
							}
						}
					}
				}
			}
		}
	`);

	const baseUrl = removeTrailingSlash(siteMetadata.organization.url);
	const courseTypeImageUrls = courseTypes.reduce((acc, { node }) => {
		const { uuid, heroImage } = node.frontmatter;
		if (!heroImage?.image?.publicURL) {
			return acc;
		}
		return { ...acc, [uuid]: `${baseUrl}${heroImage.image.publicURL}` };
	}, {});

	if (process.env.GATSBY_NOINDEX === 'true') {
		console.warn('GATSBY_NOINDEX is true; indexing will be blocked for all pages.');
	}

	if (noIndex || process.env.GATSBY_NOINDEX === 'true') {
		return NoIndex({ title });
	}

	const fullTitle = title ? `${title} | easy data solution` : siteMetadata.title;
	const url = path.startsWith('/') ? `${baseUrl}${path}` : path;
	const image = imagePath && imagePath.startsWith('/') ? `${baseUrl}${imagePath}` : imagePath;
	const logoUrl = logoPath && logoPath.startsWith('/') ? `${baseUrl}${logoPath}` : logoPath;

	return (
		<>
			<title>{fullTitle}</title>

			{[
				// don't use title and description from open directory
				{ name: `robots`, content: 'noodp' },
				{ name: `description`, content: description },
				// OpenGraph
				{ property: `og:url`, content: url },
				{ property: `og:title`, content: fullTitle },
				{ property: `og:description`, content: description },
				image ? { property: `og:image`, content: image } : undefined,
				{ property: `og:type`, content: `website` },
				{ property: `og:locale`, content: `de_DE` },
				{ property: `og:site_name`, content: siteMetadata.title },
				// Twitter
				{ name: `twitter:card`, content: `summary` },
				{ name: `twitter:title`, content: fullTitle },
				{ name: `twitter:description`, content: description },
				image ? { name: `twitter:image`, content: image } : undefined,
			]
				.filter((m) => m !== undefined)
				.map(({ name, property, content }, idx) => (
					<meta key={idx} name={name} property={property} content={content} />
				))}

			<script type="application/ld+json">
				{JSON.stringify(
					getJsonLd({
						path,
						breadcrumbItems,
						organization: siteMetadata.organization,
						courses,
						courseTypeImageUrls,
						linkedInUrl: siteMetadata.linkedInUrl,
						xingUrl: siteMetadata.xingUrl,
						logo: logoUrl,
						blogPost,
					}),
				)}
			</script>
		</>
	);
};

const NoIndex: React.FC<{ title: string }> = ({ title }) => (
	<>
		<title>{title}</title>
		<meta name="robots" content="noindex" />
	</>
);
